import { NvScene, NvSceneProvider } from "@niivue/nvreact"
import { useMemo, useRef } from "react"
import { createPortal } from "react-dom"

import { useNvScenePanelController } from "./controller"
import { HangingProtocolDialog } from "./hanging-protocol-dialog"
import { RadiologyViewerToolbar } from "./radiology-viewer-toolbar"
import { SamOverlay } from "./sam/sam-overlay"
import type { NvScenePanelProps } from "./types"
import { ViewerSliceLayoutPicker } from "./viewer-slice-layout-picker"
import { ViewerSliceSlider } from "./viewer-slice-slider"

export type { MaskDrawingConfig } from "./types"

export function NvScenePanel(props: NvScenePanelProps) {
  const controller = useNvScenePanelController(props)
  const {
    scene,
    snapshot,
    layoutName,
    activeViewerIndex,
    viewerError,
    mouseMode,
    selectedProtocolId,
    gridOpen,
    crosshairVisible,
    applyCrosshairVisible,
    protocols,
    isLoadingProtocols,
    protocolError,
    selectedProtocol,
    isMutating,
    seriesCount,
    applyGridOpen,
    applyMouseMode,
    applySelectedProtocolId,
    handleLayoutChange,
    handleCreateProtocol,
    handleEditProtocol,
    handleSetDefault,
    handleDeleteProtocol,
    applyProtocol,
    createProtocolPending,
    updateProtocolPending,
    sliceLayoutPortalsRef,
    sliceSliderPortalsRef,
    viewerSliceLayouts,
    handleViewerSliceLayoutChange,
    samPending,
    samError,
    samMode,
    getAssignedSeriesId
  } = controller

  const seriesById = useMemo(() => {
    const map = new Map<string, (typeof props.series)[number]>()
    for (const item of props.series) {
      map.set(item.id, item)
    }
    return map
  }, [props.series])

  const sceneContainerRef = useRef<HTMLDivElement>(null)

  return (
    <NvSceneProvider scene={scene}>
      <div className="h-full flex flex-col gap-1.5">
        <div className="rounded-md border border-border bg-card p-2">
          <RadiologyViewerToolbar
            isBroadcasting={snapshot.isBroadcasting}
            onToggleBroadcasting={() => scene.setBroadcasting(!snapshot.isBroadcasting)}
            mouseMode={mouseMode}
            onMouseModeChange={applyMouseMode}
            crosshairVisible={crosshairVisible}
            onToggleCrosshair={() => applyCrosshairVisible(!crosshairVisible)}
            onGridOpen={() => applyGridOpen(true)}
          />
        </div>

        <div ref={sceneContainerRef} className="relative flex-1 min-h-0 rounded-md border border-border bg-black">
          <NvScene scene={scene} className="relative h-full w-full" initialLayout={layoutName} />
          {scene.viewers.map((viewer, viewerIndex) => {
            const portalContainer = sliceLayoutPortalsRef.current.get(viewer.id)
            if (!portalContainer) return null
            const assignedSeriesId = getAssignedSeriesId(viewerIndex)
            const seriesItem = assignedSeriesId ? seriesById.get(assignedSeriesId) : null
            const description = seriesItem?.series_description ?? null
            return createPortal(
              <div className="flex items-center gap-1.5 pointer-events-none" style={{ maxWidth: "calc(100% - 8px)" }}>
                <div className="shrink-0">
                  <ViewerSliceLayoutPicker
                    currentLayoutKey={viewerSliceLayouts[viewerIndex] ?? null}
                    onLayoutChange={(layoutKey) => handleViewerSliceLayoutChange(viewerIndex, layoutKey)}
                  />
                </div>
                {description ? (
                  <span
                    className="min-w-0 truncate text-[11px] leading-tight text-white/70 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                    title={description}
                  >
                    {description}
                  </span>
                ) : null}
              </div>,
              portalContainer,
              viewer.id
            )
          })}
          {scene.viewers.map((viewer, viewerIndex) => {
            const sliderContainer = sliceSliderPortalsRef.current.get(viewer.id)
            if (!sliderContainer) return null
            return createPortal(
              <ViewerSliceSlider
                viewerIndex={viewerIndex}
                scene={scene}
                layoutKey={viewerSliceLayouts[viewerIndex] ?? null}
              />,
              sliderContainer,
              `slider-${viewer.id}`
            )
          })}
          <SamOverlay samMode={samMode} samPending={samPending} samError={samError} containerRef={sceneContainerRef} />
          {seriesCount === 0 ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
              No series available for this case.
            </div>
          ) : null}
        </div>
      </div>

      <HangingProtocolDialog
        open={gridOpen}
        onOpenChange={applyGridOpen}
        layoutName={layoutName}
        onLayoutChange={handleLayoutChange}
        activeViewerIndex={activeViewerIndex}
        viewerCount={snapshot.viewerCount}
        selectedProtocolId={selectedProtocolId}
        onSelectedProtocolIdChange={applySelectedProtocolId}
        protocols={protocols}
        selectedProtocol={selectedProtocol ?? undefined}
        isLoadingProtocols={isLoadingProtocols}
        isMutating={isMutating}
        protocolError={protocolError}
        viewerError={viewerError}
        onApply={applyProtocol}
        onSetDefault={() => void handleSetDefault()}
        onDelete={() => void handleDeleteProtocol()}
        onCreateProtocol={handleCreateProtocol}
        onEditProtocol={handleEditProtocol}
        isCreating={createProtocolPending}
        isUpdating={updateProtocolPending}
      />
    </NvSceneProvider>
  )
}
